import React, { useState } from 'react';
import {
  Icon,
  IconButton,
  MessageBar,
  MessageBarType,
  Spinner,
  Stack,
  Text,
} from '@fluentui/react';
import Label from '../../components/parts/Label';

/** Aligns with `components/parts/Label.tsx` and construction/leads modules */
export const billitRadius = {
  pill: 3,
  card: 2,
  badge: 2,
} as const;

export const billitColors = {
  green: '#71BF43',
  purple: '#581A73',
  orange: '#ff9d57',
  red: '#ff5757',
  gray: '#757575',
  text: '#241C16',
  textMuted: '#605e5c',
  neutralBg: '#EFEEEB',
  surface: '#faf9f8',
  border: '#c8c6c4',
  borderLight: '#edebe9',
  selectedBg: '#d8eacc',
  selectedBorder: '#71BF43',
  adminAccent: '#581A73',
  employeeAccent: '#71BF43',
} as const;

export type BillitStatusLabelColor = 'green' | 'blue' | 'orange' | 'red' | 'gray';

export const getBillitStatusMeta = (
  status?: string | null,
): { label: string; color: BillitStatusLabelColor } => {
  switch (status) {
    case 'assigned':
      return { label: 'Toegewezen', color: 'blue' };
    case 'subject-to-approve':
      return { label: 'Onderwerp goedkeuring', color: 'orange' };
    case 'subject-to-reject':
      return { label: 'Onderwerp afwijzing', color: 'red' };
    case 'approved':
      return { label: 'Goedgekeurd', color: 'green' };
    case 'rejected':
      return { label: 'Afgewezen', color: 'red' };
    default:
      return { label: 'Te behandelen', color: 'gray' };
  }
};

/** Fixed width so all status pills align in grid (longest label: Onderwerp goedkeuring) */
export const billitStatusPillStyle: React.CSSProperties = {
  display: 'inline-block',
  minWidth: 158,
  fontSize: 11,
  textAlign: 'center',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
};

export const BillitStatusLabel = ({
  status,
  uniformSize = false,
}: {
  status?: string | null;
  uniformSize?: boolean;
}) => {
  const meta = getBillitStatusMeta(status);
  return (
    <Label color={meta.color} style={uniformSize ? billitStatusPillStyle : undefined}>
      {meta.label}
    </Label>
  );
};

/**
 * Betaalstatus zoals Billit die kent — een andere as dan de behandelstatus
 * hierboven. Een afgewezen factuur kan betaald zijn, een goedgekeurde onbetaald.
 *
 * Bewust NIET uit `order_status` gehaald: die kolom draagt onze eigen
 * behandelstatus en het Billit-woord verdwijnt daaruit zodra iemand de factuur
 * toewijst. `paid` / `overdue` / `days_overdue` komen elke nacht uit de sync
 * en worden nooit door de app overschreven.
 */
export const getBillitPaymentMeta = (invoice: {
  paid?: boolean | null;
  overdue?: boolean | null;
  days_overdue?: number | null;
}): { label: string; color: BillitStatusLabelColor } | null => {
  if (invoice.paid === true) return { label: 'Betaald', color: 'green' };
  if (invoice.paid !== false) return null; // onbekend: liever niets dan gokken
  if (invoice.overdue) {
    return {
      label: invoice.days_overdue
        ? `Vervallen (${invoice.days_overdue} d)`
        : 'Vervallen',
      color: 'red',
    };
  }
  return { label: 'Openstaand', color: 'orange' };
};

export const BillitPaymentLabel = ({
  invoice,
  uniformSize = false,
}: {
  invoice: {
    paid?: boolean | null;
    overdue?: boolean | null;
    days_overdue?: number | null;
  };
  uniformSize?: boolean;
}) => {
  const meta = getBillitPaymentMeta(invoice);
  if (!meta) return null;
  return (
    <Label color={meta.color} style={uniformSize ? billitStatusPillStyle : undefined}>
      {meta.label}
    </Label>
  );
};

export const billitTagStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  padding: '3px 6px',
  borderRadius: billitRadius.badge,
  background: billitColors.neutralBg,
  color: billitColors.text,
};

type SectionProps = {
  title: string;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  badge?: string | number;
  action?: React.ReactNode;
  level?: 1 | 2;
};

export const BillitDetailSection = ({
  title,
  children,
  defaultCollapsed = false,
  badge,
  action,
  level = 1,
}: SectionProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <section
      style={{
        border: `1px solid ${billitColors.borderLight}`,
        borderRadius: billitRadius.card,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <button
        type='button'
        onClick={() => setCollapsed(!collapsed)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: level === 1 ? '10px 12px' : '8px 12px',
          border: 'none',
          background: billitColors.surface,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 8 }}>
          <Icon iconName={collapsed ? 'ChevronRight' : 'ChevronDown'} styles={{ root: { fontSize: 12 } }} />
          <Text style={{ fontWeight: 600, fontSize: level === 1 ? 14 : 13 }}>{title}</Text>
          {badge !== undefined && badge !== null && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: billitRadius.badge,
                background: billitColors.neutralBg,
                color: billitColors.text,
              }}
            >
              {badge}
            </span>
          )}
        </Stack>
        {action && (
          <span
            role='presentation'
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            {action}
          </span>
        )}
      </button>
      {!collapsed && <div style={{ padding: '12px' }}>{children}</div>}
    </section>
  );
};

type KvItem = { label: string; value: React.ReactNode; highlight?: boolean };

export const BillitKvGrid = ({ items }: { items: KvItem[] }) => {
  const visible = items.filter(i => i.value !== null && i.value !== undefined && i.value !== '');
  if (visible.length === 0) return null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: '10px 16px',
      }}
    >
      {visible.map(item => (
        <div key={item.label}>
          <Text style={{
 fontSize: 11, color: billitColors.textMuted, display: 'block', marginBottom: 2,
}}
          >
            {item.label}
          </Text>
          <Text
            style={{
              fontSize: 13,
              fontWeight: item.highlight ? 600 : 400,
              color: item.highlight ? billitColors.purple : billitColors.text,
            }}
          >
            {item.value}
          </Text>
        </div>
      ))}
    </div>
  );
};

type PdfEmbedProps = {
  title?: string;
  url: string | null;
  loading?: boolean;
  error?: string | null;
  height?: number | string;
  fillContainer?: boolean;
  fileName?: string;
};

export const BillitPdfEmbed = ({
  title = 'PDF',
  url,
  loading = false,
  error = null,
  height = 420,
  fillContainer = false,
  fileName = 'document.pdf',
}: PdfEmbedProps) => {
  const viewerHeight = fillContainer ? '100%' : height;
  const viewerWrapStyle: React.CSSProperties | undefined = fillContainer
    ? {
 position: 'relative', flex: 1, minHeight: 400, width: '100%',
}
    : undefined;
  const viewerInnerStyle: React.CSSProperties = fillContainer
    ? {
      border: `1px solid ${billitColors.borderLight}`,
      borderRadius: billitRadius.card,
      overflow: 'hidden',
      position: 'absolute',
      inset: 0,
      background: '#f3f2f1',
    }
    : {
      border: `1px solid ${billitColors.borderLight}`,
      borderRadius: billitRadius.card,
      overflow: 'hidden',
      height: viewerHeight,
      background: '#f3f2f1',
    };

  return (
    <Stack
      tokens={{ childrenGap: 8 }}
      styles={
        fillContainer
          ? {
            root: {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            },
          }
          : undefined
      }
    >
      <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
        <Text style={{ fontWeight: 600, fontSize: 13 }}>{title}</Text>
        {url && (
          <Stack horizontal tokens={{ childrenGap: 4 }}>
            <IconButton
              iconProps={{ iconName: 'Download' }}
              title='Download'
              onClick={() => {
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            />
            <IconButton
              iconProps={{ iconName: 'OpenInNewWindow' }}
              title='Nieuw venster'
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
            />
          </Stack>
        )}
      </Stack>
      {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
      {loading && (
        <Stack horizontalAlign='center' style={{ padding: 24 }}>
          <Spinner label='PDF laden...' />
        </Stack>
      )}
      {url && !loading && (
        <div style={viewerWrapStyle}>
          <div style={viewerInnerStyle}>
            <iframe
              src={`${url}#toolbar=1&navpanes=0&scrollbar=1`}
              style={{
 width: '100%', height: '100%', border: 'none', display: 'block',
}}
              title={title}
            />
          </div>
        </div>
      )}
      {!url && !loading && !error && (
        <Text style={{ color: billitColors.textMuted, fontSize: 12 }}>Geen PDF beschikbaar.</Text>
      )}
    </Stack>
  );
};

export const billitDetailGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 360px)',
  gap: 16,
  alignItems: 'start',
};

export const billitSummaryBarStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  padding: '12px 14px',
  background: billitColors.surface,
  border: `1px solid ${billitColors.borderLight}`,
  borderRadius: billitRadius.card,
};
